"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
class Area {
    constructor() {
        this.xMin = Number.POSITIVE_INFINITY;
        this.yMin = Number.POSITIVE_INFINITY;
        this.xMax = Number.NEGATIVE_INFINITY;
        this.yMax = Number.NEGATIVE_INFINITY;
    }
    toRectangle() {
        return {
            x1: this.xMin,
            x2: this.xMax,
            y1: this.yMin,
            y2: this.yMax,
        };
    }
    toModelBoundingBox(fallback = { minx: -100, miny: -100, maxx: 100, maxy: 100 }) {
        return {
            minx: Number.isFinite(this.xMin) ? this.xMin : fallback.minx,
            miny: Number.isFinite(this.yMin) ? this.yMin : fallback.miny,
            maxx: Number.isFinite(this.xMax) ? this.xMax : fallback.maxx,
            maxy: Number.isFinite(this.yMax) ? this.yMax : fallback.maxy,
        };
    }
    extend(v) {
        if (typeof v[0] === 'number' && typeof v[1] === 'number') {
            this.xMin = Math.min(this.xMin, v[0]);
            this.yMin = Math.min(this.yMin, v[1]);
            this.xMax = Math.max(this.xMax, v[0]);
            this.yMax = Math.max(this.yMax, v[1]);
        }
    }
    extendWithDrawing(core, drawing, warnings) {
        switch (drawing.type) {
            case 'track':
                this.extend(drawing.start);
                this.extend(drawing.end);
                break;
            case 'segment':
            case 'bus':
                this.extend(drawing.start);
                this.extend(drawing.end);
                break;
            case 'circle':
                this.extend(drawing.center.map((v) => v - drawing.radius));
                this.extend(drawing.center.map((v) => v + drawing.radius));
                break;
            case 'zone':
                drawing.polygons.forEach((z) => this.extendWithVertices(z.points));
                break;
            case 'via':
                this.extend(drawing.center.map((v) => v - drawing.diameter / 2));
                this.extend(drawing.center.map((v) => v + drawing.diameter / 2));
                break;
            case 'polygon':
                this.extendWithVertices(drawing.points);
                break;
            case 'text':
                this.extend(drawing.pos);
                if (drawing.bounds) {
                    this.extend((0, Transform_1.translatePoint)(drawing.pos, drawing.bounds));
                }
                break;
            case 'label':
                this.extend(drawing.pos);
                break;
            case 'junction':
                this.extend(drawing.position);
                break;
            case 'rect':
            case 'pin':
                this.extend(drawing.start);
                this.extend(drawing.end);
                break;
            case 'pad':
                this.extend((0, Transform_1.translatePoint)(drawing.pos, [-drawing.diameter[0] / 2, -drawing.diameter[1] / 2]));
                this.extend((0, Transform_1.translatePoint)(drawing.pos, [drawing.diameter[0] / 2, drawing.diameter[1] / 2]));
                break;
            case 'group': {
                for (const group of drawing.drawings) {
                    this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(group, {
                        pos: drawing.pos,
                        transform: {
                            angle: drawing.angle,
                        },
                    }), warnings);
                }
                break;
            }
            case 'arc': {
                this.extend((0, Transform_1.translatePoint)(drawing.center, [drawing.radius, drawing.radius]));
                this.extend((0, Transform_1.translatePoint)(drawing.center, [drawing.radius, -drawing.radius]));
                this.extend((0, Transform_1.translatePoint)(drawing.center, [-drawing.radius, drawing.radius]));
                this.extend((0, Transform_1.translatePoint)(drawing.center, [-drawing.radius, -drawing.radius]));
                break;
            }
            case 'schref':
                if (core) {
                    this.extendFromModule(core, drawing.drawModule, warnings);
                }
                else {
                    warnings.push(`Area extendWithDrawing did not have project to extend schref`);
                }
                break;
            case 'image':
                this.extend(drawing.pos);
                this.extend([drawing.pos[0] + drawing.width, drawing.pos[1] + drawing.height]);
                break;
            default:
                warnings.push(`Area extendWithDrawing does not support ${drawing.type}`);
                break;
        }
    }
    extendWithVertices(vertices) {
        vertices.forEach((v) => {
            this.extend(v);
        });
    }
    extendFromSchModel(core, model, projectLayers, warnings, includeHidden = true) {
        for (const [key, drawings] of Object.entries(model.drawings)) {
            if (!includeHidden) {
                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == key);
                if (!layerInfo || layerInfo?.defaultVisible === false) {
                    return this;
                }
            }
            for (const drawing of drawings) {
                this.extendWithDrawing(core, drawing, warnings);
            }
        }
        model.modules.forEach((module) => {
            this.extendFromModule(core, module, warnings);
        });
        return this;
    }
    extendFromModule(core, module, warnings) {
        const component = core.components[module.componentRef];
        if (!component) {
            throw new Error(`Could not find component with ref '${module.componentRef}'.`);
        }
        component.footprints.forEach((footprint) => {
            if (footprint.ref !== module.footprintRef) {
                return;
            }
            footprint.drawings.forEach((d) => {
                if (d.drawing.type === 'text') {
                    return;
                }
                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
            });
            footprint.pads.forEach((p) => {
                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(p, module), warnings);
            });
        });
        component.symbols.forEach((symbol) => {
            if (symbol.ref !== module.footprintRef) {
                return;
            }
            symbol.drawings.forEach((d) => {
                if (d.drawing.type === 'text') {
                    return;
                }
                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
            });
            symbol.pins.forEach((pin) => {
                pin.drawings?.forEach((d) => {
                    if (d.drawing.type === 'text') {
                        return;
                    }
                    this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
                });
                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(pin, module), warnings);
            });
        });
    }
    extendFromPcbModel(project, model, projectLayers, warnings, includeHidden = true) {
        Object.keys(model.tracks).forEach((layer) => {
            if (!includeHidden) {
                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == layer);
                if (!layerInfo || layerInfo.defaultVisible === false) {
                    return;
                }
            }
            model.tracks[layer].forEach((track) => {
                this.extend(track.start);
                this.extend(track.end);
            });
        });
        Object.keys(model.zones).forEach((layer) => {
            if (!includeHidden) {
                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == layer);
                if (!layerInfo || layerInfo?.defaultVisible === false) {
                    return;
                }
            }
            model.zones[layer].forEach((zone) => {
                const drawing = (0, Transform_1.toDrawing)(zone);
                if (drawing) {
                    this.extendWithDrawing(project, drawing, warnings);
                }
            });
        });
        model.vias.forEach((via) => {
            const drawing = (0, Transform_1.toDrawing)(via);
            if (drawing) {
                this.extendWithDrawing(project, drawing, warnings);
            }
        });
        model.modules.forEach((module) => {
            const footprint = project.components[module.componentRef].footprints.find((f) => f.name === module.footprintRef);
            footprint.drawings.forEach((d) => {
                this.extendWithDrawing(project, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
            });
        });
        model.edges.forEach((edge) => {
            this.extendWithDrawing(project, edge, warnings);
        });
        for (const [key, drawings] of Object.entries(model.drawings)) {
            if (!includeHidden) {
                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == key);
                if (!layerInfo || layerInfo?.defaultVisible === false) {
                    return;
                }
            }
            for (const drawing of drawings) {
                this.extendWithDrawing(project, drawing, warnings);
            }
        }
    }
    extendWithDimensions(x, y, width, height) {
        this.xMin = Math.min(this.xMin, x);
        this.yMin = Math.min(this.yMin, y);
        this.xMax = Math.max(this.xMax, x + width);
        this.yMax = Math.max(this.yMax, y + height);
    }
    extendWithArea(a) {
        this.xMin = Math.min(this.xMin, a.xMin);
        this.yMin = Math.min(this.yMin, a.yMin);
        this.xMax = Math.max(this.xMax, a.xMax);
        this.yMax = Math.max(this.yMax, a.yMax);
    }
}
exports.default = Area;
//# sourceMappingURL=Area.js.map